// src/pages/Futures/FuturesOpenOrders.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Paper, useTheme, Snackbar, Alert, IconButton, Tooltip, LinearProgress, Skeleton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchOpenFutureTrades, cancelFutureTrade, closeFutureTrade } from '../../store/Slices/FuturesTradingSlice';
import { startFuturesPriceSocket, ensurePriceSubscriptions, stopFuturesPriceSocket } from '../../services/futuresPricesWS';

const fmt = (n, d = 2) => (typeof n === 'number' && isFinite(n) ? n.toFixed(d) : '-');
const unrealized = ({ side, entry, current, qty }) =>
  (!entry || !current || !qty) ? 0 : side === 'Long' ? (current - entry) * qty : (entry - current) * qty;
const initialMargin = ({ entry, qty, lev }) => (!entry || !qty || !lev) ? 0 : (entry * qty) / lev;

export default function FuturesOpenOrders() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { data: user } = useSelector((s) => s.user);
  const { openTrades = [], openOrdersLoading, error, successMessage } = useSelector((s) => s.futures || {});
  const coinPrices = useSelector((s) => s.assets?.coinPrices || {});
  const spotTradingPairs = useSelector((s) => s.assets?.spotTradingPairs || []);

  const [snackOpen, setSnackOpen] = useState(false);

  // خريطة: trading_pair_id (_id) -> symbol
  const pairIdToSymbol = useMemo(() => {
    const map = {};
    for (const p of spotTradingPairs || []) {
      const sym = p?.symbol || (p?.base_asset_id?.symbol ? `${p.base_asset_id.symbol}USDT` : undefined);
      if (p?._id && sym) map[String(p._id)] = String(sym).toUpperCase();
    }
    return map;
  }, [spotTradingPairs]);

  // حقن الرمز لكل صف
  const withSymbols = useMemo(() => {
    const arr = Array.isArray(openTrades) ? openTrades : [];
    return arr.map((t) => {
      const fromField = t?.trading_pair_symbol && String(t.trading_pair_symbol).toUpperCase();
      const fromMap   = t?.trading_pair_id && pairIdToSymbol[String(t.trading_pair_id)];
      const fromObj   = typeof t?.trading_pair_id === 'object' && t?.trading_pair_id?.symbol && String(t.trading_pair_id.symbol).toUpperCase();
      const symbol = fromField || fromMap || fromObj || '';
      return { ...t, _symbol: symbol };
    });
  }, [openTrades, pairIdToSymbol]);

  useEffect(() => {
    if (!user?._id) return;
    dispatch(fetchOpenFutureTrades());
    startFuturesPriceSocket(dispatch);
    return () => stopFuturesPriceSocket();
  }, [dispatch, user?._id]);

  const uniqueSymbols = useMemo(() => {
    const set = new Set();
    for (const t of withSymbols) if (t._symbol) set.add(t._symbol);
    return Array.from(set);
  }, [withSymbols]);

  useEffect(() => {
    if (uniqueSymbols.length) ensurePriceSubscriptions(uniqueSymbols);
  }, [uniqueSymbols.join('|')]);

  const rows = useMemo(() => {
    return withSymbols.map((t) => {
      const symbol = t._symbol;
      const entry  = t?.open_price ?? null;
      const qty    = t?.amount ?? null;
      const lev    = t?.leverage ?? null;

      const current = symbol && typeof coinPrices[symbol] === 'number'
        ? coinPrices[symbol]
        : entry;

      const pnl = unrealized({ side: t?.position, entry, current, qty });
      const im  = initialMargin({ entry, qty, lev });
      const roe = im > 0 ? (pnl / im) * 100 : 0;

      return { ...t, symbol: symbol || '-', current, pnl, roe };
    });
  }, [withSymbols, coinPrices]);

  const refresh = () => {
    if (user?._id) dispatch(fetchOpenFutureTrades());
    if (uniqueSymbols.length) ensurePriceSubscriptions(uniqueSymbols);
  };

  useEffect(() => { if (error || successMessage) setSnackOpen(true); }, [error, successMessage]);

  const COLORS = { GREEN: '#0ECB81', RED: '#F6465D' };

  return (
    <Paper sx={{ p: 1, overflow: 'hidden', bgcolor: 'background.paper' }}>
      {openOrdersLoading && <LinearProgress sx={{ height: 2, mb: 1 }} />}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Open Futures Orders
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} sx={{ color: theme.palette.text.secondary }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ top: 0, bgcolor: 'background.paper' }}>Pair</TableCell>
              <TableCell sx={{ top: 0, bgcolor: 'background.paper' }}>Pos.</TableCell>
              <TableCell sx={{ top: 0, bgcolor: 'background.paper' }}>Margin</TableCell>
               <TableCell align="right">Lev.</TableCell>
              <TableCell align="right" sx={{ top: 0, bgcolor: 'background.paper' }}>Qty</TableCell>
              <TableCell align="right" sx={{ top: 0, bgcolor: 'background.paper' }}>Open</TableCell>
              <TableCell align="right" sx={{ top: 0, bgcolor: 'background.paper' }}>Current</TableCell>
              <TableCell align="right" sx={{ top: 0, bgcolor: 'background.paper' }}>Liq.</TableCell>
              <TableCell align="right" sx={{ top: 0, bgcolor: 'background.paper' }}>Unreal. PnL</TableCell>
              <TableCell align="right" sx={{ top: 0, bgcolor: 'background.paper' }}>ROE%</TableCell>
              <TableCell sx={{ top: 0, bgcolor: 'background.paper' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {openOrdersLoading && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {Array.from({ length: 10 }).map((__, j) => (
                  <TableCell key={j}><Skeleton variant="text" /></TableCell>
                ))}
              </TableRow>
            ))}

            {!openOrdersLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No open futures orders
                </TableCell>
              </TableRow>
            )}

            {!openOrdersLoading && rows.map((t) => (
              <TableRow key={t._id} hover>
                <TableCell>{t.symbol}</TableCell>
                <TableCell sx={{ color: t.position === 'Long' ? COLORS.GREEN : COLORS.RED }}>{t.position}</TableCell>
                <TableCell>{t.margin_type || '-'}</TableCell>
                 <TableCell align="right">{Number.isFinite(t.leverage) ? `${t.leverage}x` : '-'}</TableCell>
                <TableCell align="right">{fmt(t.amount, 4)}</TableCell>
                <TableCell align="right">{fmt(t.open_price, 2)}</TableCell>
                <TableCell align="right">{t.current != null ? fmt(t.current, 2) : '-'}</TableCell>
                <TableCell align="right">{fmt(t.liquidation_price, 2)}</TableCell>
                <TableCell align="right" sx={{ color: (t.pnl ?? 0) >= 0 ? COLORS.GREEN : COLORS.RED }}>
                  {fmt(t.pnl, 2)}
                </TableCell>
                <TableCell align="right" sx={{ color: (t.roe ?? 0) >= 0 ? COLORS.GREEN : COLORS.RED }}>
                  {fmt(t.roe, 2)}%
                </TableCell>
                <TableCell>
                  {t.status === 'Filled' ? (
                    <Button size="small" variant="contained" onClick={() => dispatch(closeFutureTrade({ trade_id: t._id }))}>
                      Close
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" color="warning" onClick={() => dispatch(cancelFutureTrade({ trade_id: t._id }))}>
                      Cancel
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert onClose={() => setSnackOpen(false)} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
          {error || successMessage}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
